import React, { useState } from "react"
import { Badge, Button, Table } from "react-bootstrap"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import moment from "moment"
import { Routes } from "@/routes"
import { FeedItem, thumbUrl } from "./types"
import UserAvatar from "./UserAvatar"
import PlaceMiniMap from "./PlaceMiniMap"
import IncidentStatusBadge from "@/components/Incidents/IncidentStatusBadge"

interface FeedCardBodyProps {
  item: FeedItem
  onOpenMedia: (index: number) => void
}

const ROUTE_STATUS_VARIANT: Record<string, string> = {
  pending: "secondary",
  in_transit: "warning",
  completed: "success",
}

const ROUTE_STATUS_LABEL: Record<string, string> = {
  pending: "Pendiente",
  in_transit: "En tránsito",
  completed: "Completado",
}

const Text = ({ value }: { value?: string | null }) =>
  value ? <p className="feed-text mb-2">{value}</p> : null

const Media = ({ item, onOpenMedia }: FeedCardBodyProps) => {
  const { t } = useTranslation("global")
  if (!item.media.length) return null
  const isPlacePhoto = item.media.every(m => m.kind === "place")
  return (
    <div className="mb-2">
      <div className={`feed-media feed-media-${Math.min(item.media.length, 2)} mb-1`}>
        {item.media.map((m, idx) => (
          <button key={idx} type="button" className="feed-media-tile" onClick={() => onOpenMedia(idx)}>
            <img src={thumbUrl(m.url)} alt="" loading="lazy" />
          </button>
        ))}
      </div>
      {isPlacePhoto && item.type !== "place_created" && (
        <div className="small text-muted">
          <i className="fas fa-image me-1" />
          {t("Foto del lugar")}
        </div>
      )}
    </div>
  )
}

const FeedCardBody = (props: FeedCardBodyProps) => (
  <>
    <FeedCardContent {...props} />
    <Media {...props} />
  </>
)

const FeedCardContent = ({ item }: FeedCardBodyProps) => {
  const { t } = useTranslation("global")
  const [showLines, setShowLines] = useState(false)
  const meta = item.meta || {}

  switch (item.type) {
    case "place_created":
      return (
        <>
          <div className="feed-title">{item.title}</div>
          {item.place && (
            <div className="text-muted small mb-2">
              {[item.place.address, item.place.sector].filter(Boolean).join(" · ")}
            </div>
          )}
          <div className="d-flex flex-wrap gap-1 mb-2">
            {item.place?.category && <Badge bg="light" text="dark">{item.place.category}</Badge>}
            {item.place?.stage && <Badge bg="info">{item.place.stage}</Badge>}
          </div>
          <Text value={item.body} />
          {item.place?.lat && item.place?.lng ? (
            <PlaceMiniMap lat={Number(item.place.lat)} lng={Number(item.place.lng)} color={item.place.assignedToColor} />
          ) : null}
        </>
      )

    case "visit":
      return (
        <>
          {meta.visitType && (
            <Badge bg="primary" className="mb-2">{meta.visitType}</Badge>
          )}
          <Text value={item.body} />
        </>
      )

    case "visit_comment":
      return (
        <>
          <Text value={item.body} />
          {(meta.visitNote || meta.visitType || meta.visitAuthorName) && (
            <div className="feed-quote">
              <div className="small w-100">
                <div className="text-muted d-flex align-items-center gap-1 flex-wrap mb-1">
                  <span>{t("En respuesta al seguimiento")}</span>
                  {meta.visitAuthorName && (
                    <>
                      <span>{t("de")}</span>
                      {meta.visitAuthorId ? (
                        <Link
                          to={`${Routes.AgentDetails.clean}${meta.visitAuthorId}`}
                          className="fw-bold text-decoration-none text-dark d-inline-flex align-items-center gap-1 feed-actor-link"
                          title={t("Ver perfil")}
                        >
                          <UserAvatar
                            name={meta.visitAuthorName}
                            image={meta.visitAuthorImage}
                            color={meta.visitAuthorColor}
                            size={18}
                          />
                          <span>{meta.visitAuthorName}</span>
                        </Link>
                      ) : (
                        <span className="fw-bold text-dark">{meta.visitAuthorName}</span>
                      )}
                    </>
                  )}
                  {meta.visitType && <span>· {meta.visitType}</span>}
                </div>
                {meta.visitNote && <div className="feed-quote-text">{meta.visitNote}</div>}
              </div>
            </div>
          )}
        </>
      )

    case "place_note":
      return (
        <>
          {item.title && <div className="feed-title">{item.title}</div>}
          <Text value={item.body} />
          {(meta.dueDate || meta.priority || meta.status) && (
            <div className="d-flex flex-wrap gap-1">
              {meta.dueDate && (
                <Badge bg="light" text="dark">
                  <i className="far fa-calendar me-1" />
                  {moment(meta.dueDate).format("DD MMM YYYY")}
                </Badge>
              )}
              {meta.priority && <Badge bg="warning" text="dark">{meta.priority}</Badge>}
              {meta.status && <Badge bg="secondary">{meta.status}</Badge>}
            </div>
          )}
        </>
      )

    case "assigned":
    case "revisit_assigned":
      return (
        <p className="feed-text mb-2 d-flex flex-wrap align-items-center gap-2">
          {item.type === "assigned" ? t("Asignó el lugar a") : t("Asignó revisita a")}
          {meta.targetName ? (
            <span className="feed-user-pill" style={{ borderColor: meta.targetColor || undefined }}>
              <UserAvatar name={meta.targetName} color={meta.targetColor} size={22} />
              <strong>{meta.targetName}</strong>
            </span>
          ) : (
            <strong>{t("Sin asignar")}</strong>
          )}
        </p>
      )

    case "route_scheduled":
      return (
        <>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            {meta.day && (
              <Badge bg="light" text="dark" className="text-capitalize">
                <i className="far fa-calendar me-1" />
                {moment(meta.day).format("dddd D MMM")}
              </Badge>
            )}
            {meta.category && (
              <Badge bg={meta.category === "dispatch" ? "warning" : "primary"} text={meta.category === "dispatch" ? "dark" : undefined}>
                {meta.category === "dispatch" ? t("Despacho") : t("Venta")}
              </Badge>
            )}
            {meta.status && (
              <Badge bg={ROUTE_STATUS_VARIANT[meta.status] || "secondary"}>
                {t(ROUTE_STATUS_LABEL[meta.status] || meta.status)}
              </Badge>
            )}
          </div>
          {meta.assignedName && (
            <div className="small mb-1 d-flex align-items-center gap-2">
              <UserAvatar name={meta.assignedName} color={meta.assignedColor} size={20} />
              {meta.assignedName}
            </div>
          )}
          {(meta.conduceNumber || meta.tripNumber || meta.invoiceNumber) && (
            <div className="small text-muted mb-1">
              {meta.conduceNumber && <span className="me-3">{t("Conduce")}: {meta.conduceNumber}</span>}
              {meta.tripNumber && <span className="me-3">{t("Viaje")}: {meta.tripNumber}</span>}
              {meta.invoiceNumber && <span>{t("Factura")}: {meta.invoiceNumber}</span>}
            </div>
          )}
          <Text value={item.body} />
        </>
      )

    case "dispatch": {
      const lines: any[] = meta.lines || []
      return (
        <>
          <div className="feed-dispatch-grid mb-2">
            <div>
              <div className="feed-kv-label">{t("Conduce")}</div>
              <div className="feed-kv-value">{meta.conduceNumber}</div>
            </div>
            <div>
              <div className="feed-kv-label">{t("Viaje")}</div>
              <div className="feed-kv-value">{(meta.tripNumbers || []).join(", ") || "—"}</div>
            </div>
            <div>
              <div className="feed-kv-label">{t("Factura")}</div>
              <div className="feed-kv-value">{(meta.invoiceNumbers || []).join(", ") || "—"}</div>
            </div>
            <div>
              <div className="feed-kv-label">{t("Almacén")}</div>
              <div className="feed-kv-value">{(meta.warehouses || []).join(", ") || "—"}</div>
            </div>
          </div>
          {!item.place && meta.clientCode && (
            <div className="small text-muted mb-2">
              {t("Cliente ERP sin lugar vinculado")}: {meta.clientCode}
            </div>
          )}
          <Button variant="link" size="sm" className="p-0" onClick={() => setShowLines(v => !v)}>
            <i className={`fas fa-chevron-${showLines ? "up" : "down"} me-1`} />
            {meta.lineCount} {t("artículos")} · {Number(meta.totalQuantity || 0).toLocaleString()} {t("unidades")}
          </Button>
          {showLines && (
            <Table size="sm" responsive className="feed-lines mt-2 mb-0">
              <thead>
                <tr>
                  <th>{t("Código")}</th>
                  <th>{t("Descripción")}</th>
                  <th className="text-end">{t("Cantidad")}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => (
                  <tr key={idx}>
                    <td className="text-nowrap">{l.itemCode}</td>
                    <td>{l.description}</td>
                    <td className="text-end text-nowrap">
                      {Number(l.quantity || 0).toLocaleString()} {l.unit}
                    </td>
                  </tr>
                ))}
                {meta.lineCount > lines.length && (
                  <tr>
                    <td colSpan={3} className="text-muted small">
                      +{meta.lineCount - lines.length} {t("más")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </>
      )
    }

    case "contact_created":
      return (
        <div className="feed-contact-box p-3 rounded-3 bg-light border mb-2">
          <div className="d-flex align-items-center gap-2 mb-2">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center text-white"
              style={{ width: 38, height: 38, backgroundColor: "#0284c7", flexShrink: 0 }}
            >
              <i className="fas fa-user-plus" />
            </div>
            <div>
              <div className="fw-bold fs-6 text-dark">{meta.contactName || item.title}</div>
              {meta.occupation && <div className="text-muted small">{meta.occupation}</div>}
            </div>
          </div>
          {(meta.phone || meta.phone2 || meta.email) && (
            <div className="d-flex flex-wrap gap-3 small pt-2 border-top">
              {meta.phone && (
                <a href={`tel:${meta.phone}`} className="text-decoration-none text-secondary d-flex align-items-center gap-1">
                  <i className="fas fa-phone-alt text-success" />
                  <span>{meta.phone}</span>
                </a>
              )}
              {meta.phone2 && (
                <a href={`tel:${meta.phone2}`} className="text-decoration-none text-secondary d-flex align-items-center gap-1">
                  <i className="fas fa-phone-alt text-success" />
                  <span>{meta.phone2}</span>
                </a>
              )}
              {meta.email && (
                <a href={`mailto:${meta.email}`} className="text-decoration-none text-secondary d-flex align-items-center gap-1">
                  <i className="fas fa-envelope text-primary" />
                  <span>{meta.email}</span>
                </a>
              )}
            </div>
          )}
        </div>
      )

    case "incident_created": {
      const incId = meta.incidentId || item.id.substring(item.id.indexOf(":") + 1)
      return (
        <div className="feed-incident-box p-3 mb-2">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="badge bg-danger text-white px-2 py-1">
                <i className="fas fa-exclamation-triangle me-1" />
                {meta.incidentType || item.title || t("Incidente")}
              </span>
              {meta.status && <IncidentStatusBadge status={meta.status} />}
              {meta.serviceType && (
                <Badge bg="light" text="dark" className="border">
                  {meta.serviceType}
                </Badge>
              )}
            </div>
            {incId && (
              <Link
                to={`${Routes.IncidentDetails.clean}${incId}`}
                className="btn btn-sm btn-outline-danger py-0 px-2"
                style={{ fontSize: "0.8rem" }}
              >
                <i className="fas fa-external-link-alt me-1" />
                {t("Ver detalle")}
              </Link>
            )}
          </div>

          <div className="feed-incident-grid mb-2">
            {meta.orderNumber && (
              <div>
                <div className="feed-kv-label">{t("No. Orden")}</div>
                <div className="feed-kv-value fw-bold text-dark">{meta.orderNumber}</div>
              </div>
            )}
            {meta.department && (
              <div>
                <div className="feed-kv-label">{t("Departamento")}</div>
                <div className="feed-kv-value">{meta.department}</div>
              </div>
            )}
            {meta.warehouse && (
              <div>
                <div className="feed-kv-label">{t("Almacén")}</div>
                <div className="feed-kv-value">{meta.warehouse}</div>
              </div>
            )}
            {(meta.customerName || meta.customerCode) && (
              <div>
                <div className="feed-kv-label">{t("Cliente")}</div>
                <div className="feed-kv-value text-truncate">
                  {meta.customerName || meta.customerCode}
                  {meta.customerCode && meta.customerName && (
                    <span className="text-muted ms-1">({meta.customerCode})</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <Text value={item.body} />
        </div>
      )
    }

    default:
      return <Text value={item.body} />
  }
}

export default FeedCardBody
